/**
 * Loading Component
 *
 * A simple loading indicator used during authentication initialization
 * and other async operations.
 */

interface LoadingProps {
  /** Optional message to display below the spinner */
  message?: string;

  /** Whether to show the component in full-screen mode */
  fullScreen?: boolean;
}

/**
 * Loading Component
 *
 * Displays a spinning loader with an optional message.
 * Can be rendered inline or as a full-screen overlay.
 */
export function Loading({
  message = 'Loading...',
  fullScreen = false,
}: LoadingProps) {
  const containerClasses = fullScreen
    ? 'loading-container loading-fullscreen'
    : 'loading-container';

  return (
    <div className={containerClasses} role="status" aria-live="polite">
      <div className="loading-spinner" aria-hidden="true">
        <svg
          className="spinner-svg"
          viewBox="0 0 50 50"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle
            className="spinner-circle"
            cx="25"
            cy="25"
            r="20"
            fill="none"
            strokeWidth="4"
          />
        </svg>
      </div>
      {message && <p className="loading-message">{message}</p>}
    </div>
  );
}

/**
 * PageLoader Component
 *
 * Full-screen loading indicator for page transitions.
 */
export function PageLoader({ message }: { message?: string }) {
  return <Loading fullScreen message={message} />;
}
